/**
 * Payment due-date forecast: anchors each contract installment to a project
 * milestone so payment flags can ride the timeline as the schedule moves.
 *
 * Anchors (matched by milestone title, case-insensitive):
 *  - 50% deposit  → contract signing date (not a milestone)
 *  - 40%          → last end date of tasks in the "Production" group when present,
 *                   else "Shipment Date" (kit) or "Production Complete" (custom)
 *  - 10%          → "Installation Complete" or "Delivery Complete"
 * Fallback when an anchor milestone is missing: the project target end date.
 *
 * Each installment's due date = anchor date + a configurable business-day lag
 * (`PaymentLagSettings`, stored at `financeSettings/paymentTerms`). Defaults:
 * 50% → +5 business days after signing, 40%/10% → +7 business days after the
 * anchor is reached.
 *
 * Pure module — no firebase imports. Firestore Timestamps are normalized via
 * duck-typed `toDate()`.
 */

import type { Milestone } from '../types';
import type {
  PaymentInstallmentId,
  PaymentScheduleBreakdown,
  PaymentScheduleRow,
} from './paymentSchedule';

export interface PaymentDueFlag {
  /** Installment id ('deposit' | 'pre_shipment' | 'final') or CO row id. */
  installmentId: string;
  kind: 'installment' | 'change_order';
  /** '50%' / '40%' / '10%' or the CO reference. */
  label: string;
  detail: string;
  amount: number;
  dueDate: Date | null;
  /** Anchor event date before the business-day lag was applied. */
  anchorDate?: Date | null;
  /** Business days between the anchor event and the due date. */
  lagBusinessDays?: number;
  anchorMilestoneId?: string;
  anchorMilestoneTitle?: string;
  /** True when 40% follows the Production task schedule instead of a milestone date. */
  anchoredToProductionTasks?: boolean;
  /** Specific task the due date tracks, when anchored to the live schedule. */
  anchorTaskTitle?: string;
  /** Live schedule task id — used to deep-link the forecast row to the timeline. */
  anchorTaskId?: string;
  /** True when the date is a fallback (target end date / no anchor found). */
  estimated: boolean;
}

/** Business-day lag between each installment's anchor event and its due date. */
export interface PaymentLagSettings {
  /** 50% deposit — business days after contract signing. */
  depositLagBusinessDays: number;
  /** 40% — business days after the production anchor is reached. */
  preShipmentLagBusinessDays: number;
  /** 10% — business days after installation/delivery completes. */
  finalLagBusinessDays: number;
}

export const DEFAULT_PAYMENT_LAG_SETTINGS: PaymentLagSettings = {
  depositLagBusinessDays: 5,
  preShipmentLagBusinessDays: 7,
  finalLagBusinessDays: 7,
};

/** Add N business days (Mon–Fri), skipping weekends. N ≤ 0 returns a copy. */
export function addBusinessDays(date: Date, businessDays: number): Date {
  const result = new Date(date);
  let remaining = Math.max(0, Math.round(businessDays));
  while (remaining > 0) {
    result.setDate(result.getDate() + 1);
    const day = result.getDay();
    if (day !== 0 && day !== 6) remaining -= 1;
  }
  return result;
}

export function installmentLagBusinessDays(
  installmentId: PaymentInstallmentId,
  lag: PaymentLagSettings,
): number {
  if (installmentId === 'deposit') return lag.depositLagBusinessDays;
  if (installmentId === 'pre_shipment') return lag.preShipmentLagBusinessDays;
  return lag.finalLagBusinessDays;
}

/** Default Project Hub task group name for production work. */
export const PRODUCTION_TASK_GROUP_NAME = 'production';

export interface TaskScheduleLike {
  id?: string;
  projectId: string;
  groupId?: string;
  parentTaskId?: string;
  title?: string;
  endDate: DateLike;
  actualEndDate?: DateLike;
  status?: string;
}

export interface TaskGroupLike {
  id?: string;
  projectId: string;
  name: string;
}

export function isProductionGroupName(name: string): boolean {
  // Substring match so "Timber Production" / "Production Schedule" also anchor the 40%.
  return (name || '').trim().toLowerCase().includes(PRODUCTION_TASK_GROUP_NAME);
}

export function isInstallGroupName(name: string): boolean {
  const n = (name || '').trim().toLowerCase();
  return n.includes('install') || n.includes('delivery');
}

/**
 * Date the Gantt and billing both use: completed work prefers actualEndDate
 * (when the task actually finished); otherwise the scheduled endDate on the task.
 */
export function taskEffectiveEndDate(task: TaskScheduleLike): Date | null {
  const scheduled = toDateSafe(task.endDate);
  const actual = toDateSafe(task.actualEndDate);
  if (task.status === 'completed' && actual) return actual;
  return scheduled ?? actual;
}

/**
 * Ids of tasks in the "Production" group, including nested subtasks of those
 * tasks. Empty set when the project has no Production group.
 */
export function getProductionTaskIds(
  projectId: string,
  tasks: TaskScheduleLike[],
  groups: TaskGroupLike[],
): Set<string> {
  const productionGroupIds = new Set(
    groups
      .filter(g => g.projectId === projectId && g.id && isProductionGroupName(g.name))
      .map(g => g.id as string),
  );
  const productionTaskIds = new Set<string>();
  if (productionGroupIds.size === 0) return productionTaskIds;

  const projectTasks = tasks.filter(t => t.projectId === projectId);
  for (const t of projectTasks) {
    if (t.id && t.groupId && productionGroupIds.has(t.groupId)) productionTaskIds.add(t.id);
  }

  let expanded = true;
  while (expanded) {
    expanded = false;
    for (const t of projectTasks) {
      if (!t.id || productionTaskIds.has(t.id)) continue;
      if (t.parentTaskId && productionTaskIds.has(t.parentTaskId)) {
        productionTaskIds.add(t.id);
        expanded = true;
      }
    }
  }
  return productionTaskIds;
}

/**
 * Latest scheduled (or actual) end across ALL of a project's tasks — the live
 * schedule end. Fallback anchor when neither a Production group nor an anchor
 * milestone exists (much closer to reality than a stale `targetEndDate`).
 */
export interface TaskEndAnchor {
  date: Date;
  taskId?: string;
  taskTitle?: string;
}

function lastTaskAnchorAmong(
  projectId: string,
  tasks: TaskScheduleLike[],
  allowedIds?: Set<string>,
): TaskEndAnchor | null {
  let best: TaskEndAnchor | null = null;
  for (const t of tasks) {
    if (t.projectId !== projectId) continue;
    if (allowedIds && (!t.id || !allowedIds.has(t.id))) continue;
    const end = taskEffectiveEndDate(t);
    if (!end) continue;
    if (!best || end.getTime() > best.date.getTime()) {
      best = { date: end, taskId: t.id, taskTitle: (t.title || '').trim() || undefined };
    }
  }
  return best;
}

export function getLastProjectTaskAnchor(
  projectId: string,
  tasks: TaskScheduleLike[],
): TaskEndAnchor | null {
  return lastTaskAnchorAmong(projectId, tasks);
}

export function getLastProjectTaskEndDate(
  projectId: string,
  tasks: TaskScheduleLike[],
): Date | null {
  return getLastProjectTaskAnchor(projectId, tasks)?.date ?? null;
}

/**
 * Latest scheduled (or actual) end among tasks in the "Production" group,
 * including nested subtasks of those tasks.
 */
export function getLastProductionTaskAnchor(
  projectId: string,
  tasks: TaskScheduleLike[],
  groups: TaskGroupLike[],
): TaskEndAnchor | null {
  const productionTaskIds = getProductionTaskIds(projectId, tasks, groups);
  if (productionTaskIds.size === 0) return null;
  return lastTaskAnchorAmong(projectId, tasks, productionTaskIds);
}

export function getLastProductionTaskEndDate(
  projectId: string,
  tasks: TaskScheduleLike[],
  groups: TaskGroupLike[],
): Date | null {
  return getLastProductionTaskAnchor(projectId, tasks, groups)?.date ?? null;
}

export function getInstallTaskIds(
  projectId: string,
  tasks: TaskScheduleLike[],
  groups: TaskGroupLike[],
): Set<string> {
  const installGroupIds = new Set(
    groups
      .filter(g => g.projectId === projectId && g.id && isInstallGroupName(g.name))
      .map(g => g.id as string),
  );
  const installTaskIds = new Set<string>();
  if (installGroupIds.size === 0) return installTaskIds;

  const projectTasks = tasks.filter(t => t.projectId === projectId);
  for (const t of projectTasks) {
    if (t.id && t.groupId && installGroupIds.has(t.groupId)) installTaskIds.add(t.id);
  }

  let expanded = true;
  while (expanded) {
    expanded = false;
    for (const t of projectTasks) {
      if (!t.id || installTaskIds.has(t.id)) continue;
      if (t.parentTaskId && installTaskIds.has(t.parentTaskId)) {
        installTaskIds.add(t.id);
        expanded = true;
      }
    }
  }
  return installTaskIds;
}

export function getLastInstallTaskAnchor(
  projectId: string,
  tasks: TaskScheduleLike[],
  groups: TaskGroupLike[],
): TaskEndAnchor | null {
  const installTaskIds = getInstallTaskIds(projectId, tasks, groups);
  if (installTaskIds.size === 0) return null;
  return lastTaskAnchorAmong(projectId, tasks, installTaskIds);
}

export function getLastInstallTaskEndDate(
  projectId: string,
  tasks: TaskScheduleLike[],
  groups: TaskGroupLike[],
): Date | null {
  return getLastInstallTaskAnchor(projectId, tasks, groups)?.date ?? null;
}

function titleMatchesAnchor(taskTitle: string, anchors: string[]): boolean {
  const n = taskTitle.trim().toLowerCase();
  if (!n) return false;
  return anchors.some(a => {
    const w = a.trim().toLowerCase();
    if (!w) return false;
    // Contains-match for longer names so "Est. Timber Package lead" hits
    // "Timber Package lead". Short titles stay exact / prefix to avoid
    // grabbing unrelated tasks.
    return n === w || n.startsWith(w) || (w.length >= 12 && n.includes(w));
  });
}

/** Task whose title matches the installment's milestone names (live schedule). */
export function findNamedInstallmentTask(
  projectId: string,
  tasks: TaskScheduleLike[],
  installmentId: Exclude<PaymentInstallmentId, 'deposit'>,
): TaskEndAnchor | null {
  const titles = NAMED_TASK_TITLES[installmentId];
  let best: TaskEndAnchor | null = null;
  for (const t of tasks) {
    if (t.projectId !== projectId) continue;
    const title = (t.title || '').trim();
    if (!titleMatchesAnchor(title, titles)) continue;
    const end = taskEffectiveEndDate(t);
    if (!end) continue;
    if (!best || end.getTime() > best.date.getTime()) {
      best = { date: end, taskId: t.id, taskTitle: title };
    }
  }
  return best;
}

export interface PaymentDueTaskAnchors {
  production?: TaskEndAnchor | null;
  install?: TaskEndAnchor | null;
  project?: TaskEndAnchor | null;
  namedPreShipment?: TaskEndAnchor | null;
  namedFinal?: TaskEndAnchor | null;
}

export function resolvePaymentDueTaskAnchors(
  projectId: string,
  tasks: TaskScheduleLike[],
  groups: TaskGroupLike[],
): PaymentDueTaskAnchors {
  return {
    production: getLastProductionTaskAnchor(projectId, tasks, groups),
    install: getLastInstallTaskAnchor(projectId, tasks, groups),
    project: getLastProjectTaskAnchor(projectId, tasks),
    namedPreShipment: findNamedInstallmentTask(projectId, tasks, 'pre_shipment'),
    namedFinal: findNamedInstallmentTask(projectId, tasks, 'final'),
  };
}

export interface DealScheduleProjectLike {
  id?: string;
  dealId?: string;
  contractedAt?: DateLike;
  targetEndDate?: DateLike;
}

/**
 * A deal can have more than one timber project (precon stub + the hub
 * project the Gantt actually shows). Finances / billables used to keep
 * whichever project last won a `dealId` map — often a stub with a stale
 * `targetEndDate` and no tasks — so the 40% fell back to "not linked to
 * a task" while the Gantt flag was correct.
 *
 * Prefer the project that has a live 40%/10% schedule (Production group,
 * named timber-package / shipment task, then any dated task).
 */
export function pickTimberProjectForDealSchedule<T extends DealScheduleProjectLike>(
  projects: T[],
  dealId: string,
  tasks: TaskScheduleLike[],
  groups: TaskGroupLike[],
): T | undefined {
  const candidates = projects.filter((p) => p.id && p.dealId === dealId);
  if (candidates.length <= 1) return candidates[0];

  const score = (p: T): number => {
    const anchors = resolvePaymentDueTaskAnchors(p.id as string, tasks, groups);
    let s = 0;
    if (anchors.production?.date) s += 1_000_000 + anchors.production.date.getTime() / 1e13;
    if (anchors.namedPreShipment?.date) {
      s += 500_000 + anchors.namedPreShipment.date.getTime() / 1e13;
    }
    const installDate = anchors.install?.date ?? anchors.namedFinal?.date;
    if (installDate) s += 100_000 + installDate.getTime() / 1e13;
    if (anchors.project?.date) s += 10_000 + anchors.project.date.getTime() / 1e13;
    if (toDateSafe(p.contractedAt)) s += 100;
    if (toDateSafe(p.targetEndDate)) s += 1;
    return s;
  };

  return candidates.slice().sort((a, b) => score(b) - score(a))[0];
}

/** Milestone titles each installment anchors to, in preference order. */
export const INSTALLMENT_ANCHOR_TITLES: Record<
  Exclude<PaymentInstallmentId, 'deposit'>,
  string[]
> = {
  pre_shipment: ['Shipment Date', 'Production Complete'],
  final: ['Installation Complete', 'Delivery Complete'],
};

const NAMED_TASK_TITLES: Record<Exclude<PaymentInstallmentId, 'deposit'>, string[]> = {
  pre_shipment: [
    ...INSTALLMENT_ANCHOR_TITLES.pre_shipment,
    'Cut Timber Package',
    'Timber Package lead',
  ],
  final: [...INSTALLMENT_ANCHOR_TITLES.final],
};

type DateLike = Date | { toDate?: () => Date } | string | number | null | undefined;

export function toDateSafe(value: DateLike): Date | null {
  if (value == null) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'object' && typeof value.toDate === 'function') {
    try {
      const d = value.toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
    } catch {
      return null;
    }
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

export interface MilestoneLike {
  id?: string;
  title: string;
  targetDate: DateLike;
  completedDate?: DateLike;
  status?: Milestone['status'];
}

/** Effective date of a milestone: actual completion when done, else target. */
export function milestoneEffectiveDate(m: MilestoneLike): Date | null {
  return toDateSafe(m.completedDate) || toDateSafe(m.targetDate);
}

export function findAnchorMilestone(
  milestones: MilestoneLike[],
  installmentId: PaymentInstallmentId,
): MilestoneLike | null {
  if (installmentId === 'deposit') return null;
  const titles = INSTALLMENT_ANCHOR_TITLES[installmentId];
  for (const title of titles) {
    const match = milestones.find(
      m => (m.title || '').trim().toLowerCase() === title.toLowerCase(),
    );
    if (match) return match;
  }
  return null;
}

export interface ProjectDatesLike {
  /** Contract signing date — proposalSignedAt / contractedAt. */
  contractSignedAt?: DateLike;
  startDate?: DateLike;
  targetEndDate?: DateLike;
}

type InstallmentDueResult = {
  dueDate: Date | null;
  /** Anchor event date before the lag was applied. */
  anchorDate: Date | null;
  lagBusinessDays: number;
  anchor: MilestoneLike | null;
  estimated: boolean;
  anchoredToProductionTasks?: boolean;
  anchorTaskTitle?: string;
  anchorTaskId?: string;
};

function fromTaskAnchor(
  task: TaskEndAnchor,
  lagBusinessDays: number,
  withLag: (d: Date | null) => Date | null,
  milestones: MilestoneLike[],
  installmentId: PaymentInstallmentId,
  estimated: boolean,
): InstallmentDueResult {
  return {
    dueDate: withLag(task.date),
    anchorDate: task.date,
    lagBusinessDays,
    anchor: installmentId === 'deposit' ? null : findAnchorMilestone(milestones, installmentId),
    estimated,
    anchoredToProductionTasks: !estimated,
    anchorTaskTitle: task.taskTitle,
    anchorTaskId: task.taskId,
  };
}

function installmentDue(
  installmentId: PaymentInstallmentId,
  project: ProjectDatesLike,
  milestones: MilestoneLike[],
  lastProductionTaskEnd?: Date | null,
  lagSettings: PaymentLagSettings = DEFAULT_PAYMENT_LAG_SETTINGS,
  lastProjectTaskEnd?: Date | null,
  taskTitles?: { production?: string; project?: string; install?: string },
  taskAnchors?: PaymentDueTaskAnchors,
): InstallmentDueResult {
  const lagBusinessDays = installmentLagBusinessDays(installmentId, lagSettings);
  const withLag = (anchorDate: Date | null): Date | null =>
    anchorDate ? addBusinessDays(anchorDate, lagBusinessDays) : null;

  if (installmentId === 'deposit') {
    const signed = toDateSafe(project.contractSignedAt) || toDateSafe(project.startDate);
    return {
      dueDate: withLag(signed),
      anchorDate: signed,
      lagBusinessDays,
      anchor: null,
      estimated: !toDateSafe(project.contractSignedAt),
    };
  }

  const production =
    taskAnchors?.production ??
    (lastProductionTaskEnd
      ? { date: lastProductionTaskEnd, taskTitle: taskTitles?.production }
      : null);
  const namedPre = taskAnchors?.namedPreShipment ?? null;
  const namedFinal = taskAnchors?.namedFinal ?? null;
  const projectTask =
    taskAnchors?.project ??
    (lastProjectTaskEnd ? { date: lastProjectTaskEnd, taskTitle: taskTitles?.project } : null);

  if (installmentId === 'pre_shipment') {
    if (production) return fromTaskAnchor(production, lagBusinessDays, withLag, milestones, installmentId, false);
    if (namedPre) return fromTaskAnchor(namedPre, lagBusinessDays, withLag, milestones, installmentId, false);
  }
  if (installmentId === 'final') {
    if (namedFinal) return fromTaskAnchor(namedFinal, lagBusinessDays, withLag, milestones, installmentId, false);
    if (taskAnchors?.install) {
      return fromTaskAnchor(taskAnchors.install, lagBusinessDays, withLag, milestones, installmentId, false);
    }
  }

  const anchor = findAnchorMilestone(milestones, installmentId);
  if (anchor) {
    const anchorDate = milestoneEffectiveDate(anchor);
    return { dueDate: withLag(anchorDate), anchorDate, lagBusinessDays, anchor, estimated: false };
  }
  // No named task / group / milestone: ride the latest task on the plan.
  if (projectTask) {
    return fromTaskAnchor(projectTask, lagBusinessDays, withLag, milestones, installmentId, true);
  }
  const fallback = toDateSafe(project.targetEndDate);
  return {
    dueDate: withLag(fallback),
    anchorDate: fallback,
    lagBusinessDays,
    anchor: null,
    estimated: true,
  };
}

/**
 * Installments whose due point has passed — used to decide which installments
 * a folded change order can still adjust. An installment has passed when its
 * anchor milestone is completed or its due date is in the past. The deposit is
 * always passed once the contract is signed (COs post-date signing).
 */
export function computePassedInstallments(
  project: ProjectDatesLike,
  milestones: MilestoneLike[],
  now: Date = new Date(),
  lastProductionTaskEnd?: Date | null,
  lastInstallTaskEnd?: Date | null,
): PaymentInstallmentId[] {
  const passed: PaymentInstallmentId[] = ['deposit'];
  (['pre_shipment', 'final'] as const).forEach(id => {
    if (id === 'pre_shipment' && lastProductionTaskEnd) {
      if (lastProductionTaskEnd.getTime() < now.getTime()) passed.push(id);
      return;
    }
    if (id === 'final' && lastInstallTaskEnd) {
      if (lastInstallTaskEnd.getTime() < now.getTime()) passed.push(id);
      return;
    }
    const anchor = findAnchorMilestone(milestones, id);
    if (anchor) {
      const completed = anchor.status === 'completed' || !!toDateSafe(anchor.completedDate);
      const due = milestoneEffectiveDate(anchor);
      if (completed || (due != null && due.getTime() < now.getTime())) passed.push(id);
    }
  });
  return passed;
}

/**
 * Map every schedule row to a due date:
 *  - installments anchor to milestones / contract dates, plus the configured
 *    business-day lag (`lagSettings`, defaults 5/7/7),
 *  - immediate change orders are due at their approval date (no lag).
 */
export function computePaymentDueForecast(
  project: ProjectDatesLike,
  milestones: MilestoneLike[],
  schedule: PaymentScheduleBreakdown,
  changeOrderApprovedAt?: Record<string, DateLike>,
  lastProductionTaskEnd?: Date | null,
  lagSettings: PaymentLagSettings = DEFAULT_PAYMENT_LAG_SETTINGS,
  /** Live schedule end — fallback anchor when no milestone/production anchor exists. */
  lastProjectTaskEnd?: Date | null,
  /** Task names for the production / schedule-end anchors (shown on the forecast). */
  taskTitles?: { production?: string; project?: string; install?: string },
  /** Live task anchors — preferred over milestone target dates when present. */
  taskAnchors?: PaymentDueTaskAnchors,
): PaymentDueFlag[] {
  return schedule.rows.map((row: PaymentScheduleRow): PaymentDueFlag => {
    if (row.kind === 'change_order') {
      const approvedAt = changeOrderApprovedAt ? toDateSafe(changeOrderApprovedAt[row.id]) : null;
      return {
        installmentId: row.id,
        kind: 'change_order',
        label: row.label,
        detail: row.detail,
        amount: row.amount,
        dueDate: approvedAt,
        estimated: !approvedAt,
      };
    }
    const { dueDate, anchorDate, lagBusinessDays, anchor, estimated, anchoredToProductionTasks, anchorTaskTitle, anchorTaskId } =
      installmentDue(
        row.id as PaymentInstallmentId,
        project,
        milestones,
        lastProductionTaskEnd,
        lagSettings,
        lastProjectTaskEnd,
        taskTitles,
        taskAnchors,
      );
    return {
      installmentId: row.id,
      kind: 'installment',
      label: row.label,
      detail: row.detail,
      amount: row.amount,
      dueDate,
      anchorDate,
      lagBusinessDays,
      anchorMilestoneId: anchor?.id,
      anchorMilestoneTitle: anchoredToProductionTasks
        ? (anchorTaskTitle || 'Production schedule')
        : anchor?.title,
      anchoredToProductionTasks,
      anchorTaskTitle,
      anchorTaskId,
      estimated,
    };
  });
}

function fmtAnchorDay(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Human-readable "why this due date" for AR installments — same anchors the
 * Billing & Payables page and Gantt flags use (contract signing, Production
 * task end, milestone, plus business-day lag).
 */
export function describeReceivableDueAnchor(flag: PaymentDueFlag): string {
  if (flag.kind === 'change_order') return flag.detail || 'due upon approval';
  const lag =
    flag.lagBusinessDays && flag.lagBusinessDays > 0
      ? `${flag.lagBusinessDays} business day${flag.lagBusinessDays === 1 ? '' : 's'} after `
      : '';
  if (flag.anchorTaskTitle && flag.anchorDate && !flag.estimated) {
    return `${lag}${flag.anchorTaskTitle} — task ends ${fmtAnchorDay(flag.anchorDate)}`;
  }
  if (flag.anchoredToProductionTasks) {
    const task = flag.anchorTaskTitle || 'Production task';
    const when = flag.anchorDate
      ? `task ends ${fmtAnchorDay(flag.anchorDate)}`
      : 'task date unknown';
    return `${lag}${task} — ${when}`;
  }
  if (flag.installmentId === 'deposit' && flag.anchorDate) {
    return `${lag}contract signing — ${fmtAnchorDay(flag.anchorDate)} (not a task)`;
  }
  if (flag.anchorMilestoneTitle) {
    const when = flag.anchorDate ? `milestone ${fmtAnchorDay(flag.anchorDate)}` : 'milestone date unknown';
    return `${lag}${flag.anchorMilestoneTitle} — ${when}`;
  }
  if (flag.anchorTaskTitle && flag.anchorDate) {
    return `${lag}${flag.anchorTaskTitle} — task ends ${fmtAnchorDay(flag.anchorDate)} (estimated)`;
  }
  if (flag.anchorDate) {
    return `not linked to a task — using schedule end ${fmtAnchorDay(flag.anchorDate)} (estimated)`;
  }
  return 'not linked to a task';
}
